import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { Calendar, Gauge, Fuel, Settings, ChevronRight } from 'lucide-react';
import VehicleImageCarousel from './VehicleImageCarousel';

const VehicleSlider = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        // On récupère les 10 véhicules les plus récents
        const records = await pb.collection('car_listings').getList(1, 10, {
          filter: 'status = "available"',
          sort: '-created',
        });
        setVehicles(records.items);
      } catch (err) {
        console.error('Erreur de récupération des annonces:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  if (loading || vehicles.length === 0) return null;

  // Pour l'effet infini, on double la liste
  const slidingVehicles = [...vehicles, ...vehicles];

  return (
    <section className="vehicle-slider-section">
      <div className="slider-header">
        <span className="badge">Nos Dernières Occasions</span>
        <h2 className="title">Trouvez votre prochain véhicule</h2>
      </div>

      {/* Slider Marquee Infini */}
      <div className="slider-wrapper">
        <div className="slider-track">
          {slidingVehicles.map((car, index) => (
            <div 
              key={`${car.id}-${index}`} 
              className="car-card"
              onClick={() => navigate(`/vente-auto/${car.id}`)}
            >
              <div className="car-image-box">
                <VehicleImageCarousel 
                  images={[...(car.cover_image ? [car.cover_image] : []), ...(car.images || [])]} 
                  carId={car.id} 
                  altText={`${car.make} ${car.model}`}
                  thumbSize="400x300"
                />
                <div className="price-badge">{car.price} €</div>
              </div>
              
              <div className="car-info">
                <h3 className="car-title">{car.title}</h3>
                <span className="car-subtitle">{car.make} {car.model}</span>
                
                <div className="car-specs">
                  <div className="spec-item">
                    <Calendar size={16} />
                    <span>{car.year}</span>
                  </div>
                  <div className="spec-item">
                    <Gauge size={16} />
                    <span>{car.mileage} km</span>
                  </div>
                  <div className="spec-item">
                    <Fuel size={16} />
                    <span>{car.fuel_type || '-'}</span>
                  </div>
                  <div className="spec-item">
                    <Settings size={16} />
                    <span>{car.transmission === 'automatic' || car.transmission === 'Automatique' ? 'Auto' : 'Manuelle'}</span>
                  </div>
                </div>

                <button className="view-btn">
                  Découvrir
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="slider-footer">
        <button className="view-all-btn shadow-lg" onClick={() => navigate('/vente-auto')}>
          Voir toutes nos annonces
        </button>
      </div>

      <style jsx>{`
        .vehicle-slider-section {
          padding: 100px 0;
          background-color: #f8fafc;
          overflow: hidden;
        }

        .slider-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .badge {
          display: inline-block;
          background: #3b82f6;
          color: white;
          padding: 6px 18px;
          border-radius: 50px;
          font-weight: 800;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }

        .title {
          font-size: 2.8rem;
          color: #1e293b;
          font-weight: 900;
          margin: 0;
        }

        /* Slider Wrapper */
        .slider-wrapper {
          position: relative;
          width: 100%;
          padding: 20px 0;
        }

        .slider-track {
          display: flex;
          width: max-content;
          animation: scroll 35s linear infinite;
          gap: 30px;
          padding-left: 30px;
        }

        .slider-wrapper:hover .slider-track {
          animation-play-state: paused;
        }

        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-50% - 15px)); }
        }

        .car-card {
          width: 320px;
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(0,0,0,0.02);
          flex-shrink: 0;
          cursor: pointer;
        }

        .car-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }

        .car-image-box {
          position: relative;
          height: 220px;
          background: #f1f5f9;
        }

        .price-badge {
          position: absolute;
          bottom: 15px;
          right: 15px;
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(10px);
          color: white;
          padding: 8px 16px;
          border-radius: 12px;
          font-weight: 900;
          font-size: 1.1rem;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .car-info {
          padding: 25px;
        }

        .car-title {
          font-size: 1.2rem;
          color: #1e293b;
          font-weight: 900;
          margin: 0 0 5px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .car-subtitle {
          color: #64748b;
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: block;
          margin-bottom: 20px;
        }

        .car-specs {
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #f1f5f9;
          padding-top: 15px;
          margin-bottom: 25px;
        }

        .spec-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 0.85rem;
        }

        .spec-item span { font-weight: 700; color: #1e293b; }

        .view-btn {
          width: 100%;
          padding: 12px;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          color: #1e293b;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          cursor: pointer;
          transition: 0.3s;
        }

        .car-card:hover .view-btn {
          background: #1e293b;
          color: white;
          border-color: #1e293b;
        }

        .slider-footer {
          text-align: center;
          margin-top: 40px;
        }

        .view-all-btn {
          background: #3b82f6;
          color: white;
          padding: 16px 36px;
          border-radius: 12px;
          border: none;
          font-weight: 800;
          font-size: 1.05rem;
          cursor: pointer;
          transition: 0.3s;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .view-all-btn:hover {
          background: #2563eb;
          transform: translateY(-3px);
        }

        @media (max-width: 768px) {
          .title { font-size: 2.2rem; }
          .car-card { width: 280px; }
        }
      `}</style>
    </section>
  );
};

export default VehicleSlider;
